import { HttpClient } from "@angular/common/http";

import { Observable, catchError, forkJoin, map, of, take } from "rxjs";

interface TranslationObject {
  [key: string]: string | TranslationObject;
}

export abstract class AbstractTranslateService {
  public abstract init(): void;

  public abstract translate<T = string>(key: string, params?: Record<string, unknown>): T;
}

export class TranslateService extends AbstractTranslateService {
  private readonly paths: string[][] = [
    ["core", "base"],
    ["core", "errors"],
    ["game", "mines_tnt"],
  ];

  private dict: TranslationObject = {};

  private readonly TX = /\{\{\s*([\w.-]+)\s*\}\}/g;

  constructor(
    private readonly http: HttpClient,
    private readonly path: string,
  ) {
    super();
  }

  public init(): void {
    const language = this.language;

    document.documentElement.lang = language;

    this.load(language)
      .pipe(
        take(1),
        catchError(() => of({})),
      )
      .subscribe(translation => (this.dict = { ...translation }));
  }

  public translate<T = string>(key: string, params: Record<string, unknown> = {}): T {
    const value = this.resolve<string | TranslationObject>(this.dict, key);

    if (value === undefined) {
      return key as T;
    }

    if (typeof value === "string" && Object.keys(params).length > 0) {
      return this.interpolate(value, params) as T;
    }

    return value as T;
  }

  private load(language: string): Observable<TranslationObject> {
    return forkJoin(
      this.paths.map(paths => {
        const path = `${this.path}/${language}/${paths.join("/")}.json`;
        return this.http.get<TranslationObject>(path);
      }),
    ).pipe(
      map(parts =>
        parts.reduce<TranslationObject>((acc, part) => {
          return this.deepMerge(acc, part as Record<string, unknown>) as TranslationObject;
        }, {}),
      ),
    );
  }

  private resolve<T>(dict: TranslationObject, key: string): T | undefined {
    return key.split(".").reduce<unknown>((acc, part) => (acc as Record<string, unknown> | undefined)?.[part], dict) as T | undefined;
  }

  private isObject(value: unknown): value is Record<string, unknown> {
    return !!(value && typeof value === "object");
  }

  private deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = { ...target };

    for (const key of Object.keys(source)) {
      const srcVal = source[key];
      const tgtVal = result[key];

      if (this.isObject(srcVal) && this.isObject(tgtVal)) {
        result[key] = this.deepMerge(tgtVal, srcVal);
      } else {
        result[key] = srcVal;
      }
    }

    return result;
  }

  private interpolate(text: string, params: Record<string, unknown>): string {
    return text.replace(this.TX, (_m, name: string) => {
      const value = params[name];

      return value === undefined || value === null ? "" : String(value);
    });
  }

  private get language(): string {
    return new URLSearchParams(window.location.search).get("language")?.toLowerCase() || "en";
  }
}
